import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../../contexts/AppContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsPartner } from '../hooks/useIsPartner';
import Spinner from '../../../components/ui/Spinner';
import { AppIcon } from '../../../components/icons/AppIcon';
import { MOBILE_SHELL_TOP_SAFE_CLASS } from '../../../components/layout/layoutNavTokens';
import { cn } from '@/lib/utils';
import { PartnerSidebar } from '@/features/partner/layout/PartnerSidebar';

interface PartnerLayoutProps {
  children: React.ReactNode;
}

export function PartnerLayout({ children }: PartnerLayoutProps) {
  const { isPartner, isLoading: isPartnerLoading, loadFailed } = useIsPartner();
  const { isAuthLoading } = useAuth();
  const { setCurrentPage, currentPage } = useAppContext();
  const [navOpen, setNavOpen] = useState(false);

  const isLoading = isAuthLoading || isPartnerLoading;

  useEffect(() => {
    setNavOpen(false);
  }, [currentPage]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [navOpen]);

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background" role="status" aria-live="polite">
        <Spinner color="primary" size="lg" />
        <span className="sr-only">Carregando portal parceiro</span>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-s4 bg-background px-s6 text-center">
        <p className="max-w-sm text-sm text-secondary">
          Não foi possível carregar o portal de parceiros. Tente novamente.
        </p>
        <button
          type="button"
          className="min-h-11 touch-manipulation text-sm font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setCurrentPage('dashboard')}
        >
          Voltar ao CRM
        </button>
      </main>
    );
  }

  if (!isPartner) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background" role="status" aria-live="polite">
        <Spinner color="primary" size="lg" />
        <span className="sr-only">Verificando acesso de parceiro</span>
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col bg-background md:flex-row">
      <header
        className={cn(
          'flex shrink-0 items-center gap-s3 border-b border-subtle bg-card px-s4 py-s3 md:hidden',
          MOBILE_SHELL_TOP_SAFE_CLASS,
        )}
      >
        <button
          type="button"
          className="inline-flex size-11 touch-manipulation items-center justify-center rounded-lg text-secondary transition-colors hover:bg-surface-subtle hover:text-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-expanded={navOpen}
          aria-controls="partner-nav"
          aria-label={navOpen ? 'Fechar menu' : 'Abrir menu'}
          onClick={() => setNavOpen((v) => !v)}
        >
          <AppIcon name={navOpen ? 'close' : 'menu'} className="size-5" />
        </button>
        <p className="min-w-0 truncate text-sm font-semibold text-default">Portal Parceiro</p>
      </header>

      {navOpen ? (
        <button
          type="button"
          className="overlay-premium fixed inset-0 z-40 md:hidden"
          aria-label="Fechar menu"
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <PartnerSidebar open={navOpen} onClose={() => setNavOpen(false)} />

      <main className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain p-s4 pb-[max(1rem,env(safe-area-inset-bottom))] md:p-s6">
        {children}
      </main>
    </div>
  );
}
